import React, { useState, useRef, useEffect } from 'react';
import { LinkIcon, PdfIcon, TextIcon, ArrowIcon } from './MobileIcons';

function Eyebrow({ children }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      fontSize: 11, fontWeight: 700, letterSpacing: '0.12em',
      textTransform: 'uppercase', color: 'var(--cv-text-muted)',
      marginBottom: 8,
    }}>
      <span style={{
        display: 'inline-block', width: 14, height: 2,
        background: 'var(--cv-amber)', borderRadius: 2, flexShrink: 0,
      }} />
      {children}
    </div>
  );
}

function SpinnerIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ animation: 'msModalSpin 0.8s linear infinite' }}>
      <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5" strokeDasharray="28" strokeDashoffset="10" strokeLinecap="round" />
      <style>{`@keyframes msModalSpin { to { transform: rotate(360deg); } }`}</style>
    </svg>
  );
}

const TAB_ORDER = ['link', 'text', 'pdf'];

function AddJobModal({ onClose, onAdd, laedt, initialTab = 'link', initialPrefill = '', hatProfile = true, onProfilErstellen }) {
  const [tab, setTab] = useState(initialTab);
  const [url, setUrl] = useState(initialTab === 'link' ? initialPrefill : '');
  const [text, setText] = useState(initialTab === 'text' ? initialPrefill : '');
  const [pdfFile, setPdfFile] = useState(null);
  const [urlFocused, setUrlFocused] = useState(false);
  const [textFocused, setTextFocused] = useState(false);
  const fileRef = useRef(null);
  const panelRef = useRef(null);
  const swipeRef = useRef({ x: 0, y: 0, decided: false, isHoriz: false });
  const tabRef = useRef(tab);
  tabRef.current = tab;

  useEffect(() => {
    const el = panelRef.current;
    if (!el) return;

    const onStart = (e) => {
      swipeRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY, decided: false, isHoriz: false };
    };

    const onMove = (e) => {
      const s = swipeRef.current;
      const dx = e.touches[0].clientX - s.x;
      const dy = e.touches[0].clientY - s.y;
      if (!s.decided && (Math.abs(dx) > 6 || Math.abs(dy) > 6)) {
        s.decided = true;
        s.isHoriz = Math.abs(dx) > Math.abs(dy);
      }
      if (s.isHoriz) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    const onEnd = (e) => {
      const s = swipeRef.current;
      if (!s.isHoriz) return;
      const dx = e.changedTouches[0].clientX - s.x;
      if (Math.abs(dx) < 40) return;
      const idx = TAB_ORDER.indexOf(tabRef.current);
      if (dx < 0 && idx < TAB_ORDER.length - 1) setTab(TAB_ORDER[idx + 1]);
      if (dx > 0 && idx > 0) setTab(TAB_ORDER[idx - 1]);
    };

    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd, { passive: true });
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
    };
  }, []);

  const handleUrlPaste = (e) => {
    const clip = (e.clipboardData || window.clipboardData)?.getData('text') ?? '';
    if (!clip.trim()) return;
    const isUrl = /^https?:\/\//i.test(clip.trim());
    if (!isUrl && clip.trim().length > 40) {
      e.preventDefault();
      setText(clip.trim());
      setTab('text');
    }
  };

  const hasContent = tab === 'link' ? url.trim().length > 0
    : tab === 'pdf' ? pdfFile !== null
    : text.trim().length > 0;
  const canSubmit = hatProfile && hasContent;

  const TABS = [
    { id: 'link', label: 'Link', Icon: LinkIcon },
    { id: 'text', label: 'Text', Icon: TextIcon },
    { id: 'pdf',  label: 'PDF',  Icon: PdfIcon  },
  ];

  return (
    <div
      data-swipe-internal
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        background: 'rgba(0,16,8,0.5)',
        backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)',
        padding: '0 14px calc(env(safe-area-inset-bottom, 0px) + 20px)',
        boxSizing: 'border-box',
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: '100%', maxWidth: 520,
          background: 'var(--cv-bg-surface)',
          borderRadius: 28,
          padding: '16px 20px 28px',
          boxShadow: '0 8px 48px rgba(0,0,0,0.22), 0 2px 12px rgba(0,0,0,0.1)',
          animation: 'msBlup 0.38s cubic-bezier(0.32, 0.72, 0, 1)',
        }}
        ref={panelRef}
        onClick={e => e.stopPropagation()}
      >
        {/* Drag handle */}
        <div style={{
          width: 36, height: 4, background: 'var(--cv-border)',
          borderRadius: 999, margin: '0 auto 20px',
        }} />

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 22 }}>
          <div>
            <div style={{
              fontFamily: 'var(--cv-font-display)',
              fontSize: 26, fontWeight: 400, color: 'var(--cv-text)',
              letterSpacing: -0.5, lineHeight: 1.1,
            }}>
              Neue{' '}
              <em style={{ fontStyle: 'italic', color: 'var(--cv-amber)' }}>Stelle</em>
            </div>
            <div style={{ fontSize: 13, color: 'var(--cv-text-muted)', marginTop: 5, lineHeight: 1.4 }}>
              Wähle, wie du die Stelle hinzufügen willst.
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              width: 32, height: 32, borderRadius: 999, flexShrink: 0, marginTop: 2,
              background: 'var(--cv-teal-soft)', border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
              <path d="M1 1l9 9M10 1L1 10" stroke="var(--cv-teal)" strokeWidth="1.7" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        {/* Tab Selector */}
        <div style={{
          display: 'flex', gap: 4, marginBottom: 24,
          background: 'rgba(0,0,0,0.05)', borderRadius: 20, padding: 4,
        }}>
          {TABS.map(({ id, label, Icon }) => {
            const active = tab === id;
            return (
              <button
                key={id}
                onClick={() => setTab(id)}
                style={{
                  flex: 1, padding: '10px 6px', fontFamily: 'inherit',
                  background: active ? 'var(--cv-bg-elevated)' : 'transparent',
                  border: active ? '1px solid var(--cv-border-warm)' : '1px solid transparent',
                  borderRadius: 16,
                  fontSize: 12, fontWeight: active ? 700 : 500,
                  color: active ? 'var(--cv-teal)' : 'var(--cv-text-muted)',
                  cursor: 'pointer', gap: 5,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: active ? '0 2px 12px var(--cv-amber-soft), 0 1px 4px rgba(0,0,0,0.06)' : 'none',
                  transition: 'all 0.18s var(--cv-ease)',
                  WebkitTapHighlightColor: 'transparent',
                }}
              >
                <Icon color={active ? 'var(--cv-teal)' : 'var(--cv-text-muted)'} />
                {label}
              </button>
            );
          })}
        </div>

        {/* Tab: Link */}
        {tab === 'link' && (
          <>
            <Eyebrow>URL der Stellenanzeige</Eyebrow>
            <input
              value={url}
              onChange={e => setUrl(e.target.value)}
              placeholder="https://…"
              autoFocus
              onPaste={handleUrlPaste}
              onFocus={() => setUrlFocused(true)}
              onBlur={() => setUrlFocused(false)}
              style={{
                width: '100%', padding: '14px 16px', boxSizing: 'border-box',
                border: `1.5px solid ${urlFocused ? 'rgba(255,188,0,0.6)' : url ? 'var(--cv-border-warm)' : 'var(--cv-border)'}`,
                borderRadius: 'var(--cv-radius-input)',
                background: 'var(--cv-bg-elevated)',
                fontFamily: 'inherit', fontSize: 15, color: 'var(--cv-text)',
                outline: 'none',
                boxShadow: urlFocused ? '0 0 0 3px var(--cv-amber-soft)' : 'none',
                transition: 'border-color 0.15s, box-shadow 0.15s',
              }}
            />
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10,
              margin: '12px 0 24px', padding: '11px 14px',
              background: 'var(--cv-teal-soft)',
              borderRadius: 14, border: '1px solid var(--cv-border-teal)',
            }}>
              <div style={{
                width: 26, height: 26, borderRadius: 999, flexShrink: 0,
                background: 'var(--cv-teal)', display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
                  <path d="M6 1l1.1 3.1H10L7.6 5.9l.9 2.9L6 7.1 3.5 8.8l.9-2.9L2 4.1h2.9L6 1z" fill="var(--cv-amber)"/>
                </svg>
              </div>
              <span style={{ fontSize: 12, color: 'var(--cv-teal)', lineHeight: 1.45 }}>
                <strong>Unterstützt</strong> LinkedIn, StepStone, Xing, Indeed &amp; direkte Karriereseiten.
              </span>
            </div>
          </>
        )}

        {/* Tab: PDF */}
        {tab === 'pdf' && (
          <>
            <Eyebrow>Stellenanzeige als PDF</Eyebrow>
            <input
              ref={fileRef}
              type="file"
              accept=".pdf"
              style={{ display: 'none' }}
              onChange={e => setPdfFile(e.target.files?.[0] || null)}
            />
            <button
              onClick={() => fileRef.current?.click()}
              style={{
                width: '100%', padding: '28px 20px', marginBottom: 24, boxSizing: 'border-box',
                border: `2px dashed ${pdfFile ? 'var(--cv-amber)' : 'var(--cv-border)'}`,
                borderRadius: 18,
                background: pdfFile ? 'var(--cv-amber-soft)' : 'var(--cv-bg-elevated)',
                cursor: 'pointer', fontFamily: 'inherit',
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10,
                WebkitTapHighlightColor: 'transparent',
                transition: 'all 0.18s var(--cv-ease)',
                boxShadow: pdfFile ? 'var(--cv-shadow-amber)' : 'none',
              }}
            >
              <div style={{
                width: 52, height: 52, borderRadius: 14,
                background: pdfFile ? 'var(--cv-amber)' : 'var(--cv-teal-soft)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'background 0.18s',
              }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8L14 2z"
                    stroke="var(--cv-teal)" strokeWidth="1.5" strokeLinejoin="round"/>
                  <path d="M14 2v6h6M12 13v6M9.5 15.5L12 13l2.5 2.5"
                    stroke="var(--cv-teal)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--cv-text)', letterSpacing: -0.2 }}>
                {pdfFile ? pdfFile.name : 'PDF hochladen'}
              </div>
              <div style={{ fontSize: 12, color: 'var(--cv-text-muted)' }}>
                {pdfFile ? 'Tippe zum Ändern' : 'Tippe um eine Datei auszuwählen'}
              </div>
            </button>
          </>
        )}

        {/* Tab: Text */}
        {tab === 'text' && (
          <>
            <Eyebrow>Stellenbeschreibung</Eyebrow>
            <textarea
              value={text}
              onChange={e => setText(e.target.value)}
              placeholder="Füge hier den Text der Stellenanzeige ein…"
              rows={6}
              onFocus={() => setTextFocused(true)}
              onBlur={() => setTextFocused(false)}
              style={{
                width: '100%', padding: '14px 16px', boxSizing: 'border-box',
                border: `1.5px solid ${textFocused ? 'rgba(255,188,0,0.6)' : text ? 'var(--cv-border-warm)' : 'var(--cv-border)'}`,
                borderRadius: 'var(--cv-radius-input)',
                background: 'var(--cv-bg-elevated)',
                fontFamily: 'inherit', fontSize: 14, color: 'var(--cv-text)',
                outline: 'none', resize: 'none', lineHeight: 1.6,
                boxShadow: textFocused ? '0 0 0 3px var(--cv-amber-soft)' : 'none',
                transition: 'border-color 0.15s, box-shadow 0.15s',
                marginBottom: 24,
              }}
            />
          </>
        )}

        {/* No-profile hint */}
        {!hatProfile && (
          <div
            onClick={onProfilErstellen}
            role={onProfilErstellen ? 'button' : undefined}
            style={{
              marginBottom: 12, padding: '10px 14px', borderRadius: 12,
              background: 'rgba(220,38,38,0.07)', border: '1px solid rgba(220,38,38,0.18)',
              fontSize: 13, color: '#B91C1C', lineHeight: 1.5, textAlign: 'center',
              cursor: onProfilErstellen ? 'pointer' : 'default',
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            Erstelle zuerst ein <strong style={{ textDecoration: onProfilErstellen ? 'underline' : 'none' }}>Profil</strong>, bevor du eine Bewerbung generierst.
          </div>
        )}

        {/* Submit */}
        <button
          onClick={() => onAdd({ type: tab, url: url.trim(), text: text.trim(), pdfFile })}
          disabled={laedt || !canSubmit}
          style={{
            width: '100%', padding: '17px',
            background: (laedt || !canSubmit) ? 'rgba(0,0,0,0.06)' : 'var(--cv-amber)',
            color: (laedt || !canSubmit) ? 'var(--cv-text-muted)' : 'var(--cv-teal)',
            border: 'none',
            borderRadius: 'var(--cv-radius-pill)',
            fontFamily: 'inherit', fontSize: 16, fontWeight: 700, letterSpacing: -0.2,
            cursor: (laedt || !canSubmit) ? 'default' : 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            boxShadow: (laedt || !canSubmit) ? 'none' : 'var(--cv-shadow-amber)',
            transition: 'background 0.18s, color 0.18s, box-shadow 0.18s',
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          {laedt
            ? <><SpinnerIcon /> {tab === 'pdf' ? 'PDF wird gelesen…' : 'Erstelle…'}</>
            : <>Stelle hinzufügen <ArrowIcon color={(laedt || !canSubmit) ? 'var(--cv-text-muted)' : 'var(--cv-teal)'} /></>
          }
        </button>
      </div>
    </div>
  );
}

export default AddJobModal;
